'use client';

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import EmailCollector from '@/components/EmailCollector';
import { isPopupSuppressedPath } from '@/lib/checkout/popup-suppressed-routes';
import { safeGetItem, safeSetItem, STORAGE_KEYS } from '@/utils/storage/safeStorage';

// Configuration
const BOOK_PREVIEW_DELAY_MS = 120000; // 2 minutes for book cover preview
const DEFAULT_DELAY_MS = 30000; // 30 seconds for other pages

interface EmailCollectorContextType {
  showEmailCollector: boolean;
  closeEmailCollector: () => void;
  permanentlyDisableEmailCollector: () => void;
}

const EmailCollectorContext = createContext<EmailCollectorContextType>({
  showEmailCollector: false,
  closeEmailCollector: () => {},
  permanentlyDisableEmailCollector: () => {},
});

export const useEmailCollector = () => useContext(EmailCollectorContext);

export function EmailCollectorProvider({ children }: { children: React.ReactNode }) {
  const [showEmailCollector, setShowEmailCollector] = useState(false);
  const [shouldNeverShow, setShouldNeverShow] = useState(false);
  const [closedInSession, setClosedInSession] = useState(false);
  const pathname = usePathname();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check if we're on a page where the popup should be disabled
  const isDisabledPage = () => {
    if (!pathname) return false;
    
    // Never over the payment flow (3DS modal, card entry, thank-you page)
    if (isPopupSuppressedPath(pathname)) return true;
    
    // Disable on the create-book wizard (the only create-book page; the old sub-pages redirect to it)
    const wizardPaths = ['/create-book'];
    
    return wizardPaths.some(path => pathname.startsWith(path));
  };
  
  // Get delay based on current page
  const getDelayForPage = () => {
    if (!pathname) return DEFAULT_DELAY_MS;
    
    // 2 minutes for book cover preview page
    if (pathname.includes('/book/') && pathname.includes('/preview')) {
      return BOOK_PREVIEW_DELAY_MS;
    }
    
    return DEFAULT_DELAY_MS;
  };
  
  // Close email collector
  const closeEmailCollector = () => {
    setShowEmailCollector(false);
    
    // Remember that user closed it for this session
    safeSetItem(STORAGE_KEYS.EMAIL_POPUP_SHOWN, 'true', { useSessionStorage: true });
    setClosedInSession(true);
    
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  
  // Permanently disable email collector (when email is provided)
  const permanentlyDisableEmailCollector = () => {
    safeSetItem(STORAGE_KEYS.EMAIL_COLLECTED, 'true');
    setShouldNeverShow(true);
    closeEmailCollector();
  };
  
  // Handle email collector success
  const handleEmailCollectorSuccess = () => {
    permanentlyDisableEmailCollector();
  };
  
  // Check permanent flags on mount
  useEffect(() => {
    // Check if user has already provided email
    const hasProvidedEmail = safeGetItem(STORAGE_KEYS.EMAIL_COLLECTED, { 
      fallbackValue: 'false' 
    }) === 'true';
    
    // Check if user has verified they are human
    const hasVerifiedHuman = safeGetItem(STORAGE_KEYS.HUMAN_VERIFIED, { 
      fallbackValue: 'false' 
    }) === 'true';
    
    // Check if user closed popup in this session
    const hasClosedInSession = safeGetItem(STORAGE_KEYS.EMAIL_POPUP_SHOWN, { 
      useSessionStorage: true,
      fallbackValue: 'false' 
    }) === 'true';
    
    // If either permanent flag is true, never show the popup
    if (hasProvidedEmail || hasVerifiedHuman) {
      setShouldNeverShow(true);
    }
    
    // If closed in session, remember that
    if (hasClosedInSession) {
      setClosedInSession(true);
    }
  }, []);
  
  // Handle page changes and timing
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Don't set timeout if we should never show
    if (shouldNeverShow) return;
    
    // Don't set timeout if user closed it in this session
    if (closedInSession) return;
    
    // Don't set timeout on disabled pages
    if (isDisabledPage()) return;
    
    // Get delay for current page
    const delay = getDelayForPage();
    
    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      // Double-check conditions before showing
      if (!shouldNeverShow && !closedInSession && !isDisabledPage()) {
        setShowEmailCollector(true);
      }
    }, delay);
    
    // Cleanup on unmount or page change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [pathname, shouldNeverShow, closedInSession]);
  
  return (
    <EmailCollectorContext.Provider 
      value={{ 
        showEmailCollector, 
        closeEmailCollector,
        permanentlyDisableEmailCollector 
      }}
    >
      {children}
      
      {/* Global Email Collector Modal */}
      {showEmailCollector && !isDisabledPage() && (
        <EmailCollector
          source="global_footer"
          title="Stay in the Magic!"
          description="Get exclusive discounts, early access to new features, and magical story ideas delivered to your inbox."
          buttonText="Subscribe to Newsletter"
          showAsModal={true}
          onClose={closeEmailCollector}
          onSuccess={handleEmailCollectorSuccess}
        />
      )}
    </EmailCollectorContext.Provider>
  );
}
